import {TranslationObject} from '@colonist/i18n'
import {TimeUtils} from '../../../../../shared/utils/TimeUtils'
import {GameColor} from '../../../../shared/scripts/GameColor'
import {GameFont} from '../../../../shared/scripts/GameFont'
import {SpriteView} from '../../../../shared/scripts/ui_canvas_engine/SpriteView'
import {TextView} from '../../../../shared/scripts/ui_canvas_engine/TextView'
import {OpacityAnimationWrapper} from '../../../../shared/scripts/ui_game/ui_game_animations/OpacityAnimationWrapper'
import {GameEndTimeAndTurnsView} from '../../../../shared/scripts/ui_game/ui_game_end_screen/GameEndTimeAndTurnsView'
import {HideableEndGameSection, UIGameEndController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameEndController'
import {UITGameEndStatsController} from './UITGameEndStatsController'
import {UIGameRankedEndController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameRankedEndController'
import {Sprites} from '../../../../shared/scripts/ui_game/UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../../../shared/scripts/UIGameSpriteHandler'
import {PIXI} from '@colonist/pixi'
import {UIGameManager} from '../../../../shared/scripts/ui_game/UIGameManager'
import {EndGameState} from '@colonist/game-states'
import {EndGameRankedUserStateTransferObject} from '@colonist/game-base-types'
import {GameStateEnums} from '../../../../../shared/GameEnums'

export class UITGameEndController extends UIGameEndController {
    constructor(uiGameManager: UIGameManager, endGameState: EndGameState, isReplayAvailable: boolean, rankedUserStates: EndGameRankedUserStateTransferObject[] | undefined, isShownAfterLiveGame: boolean | undefined) {
        super(uiGameManager, endGameState, isReplayAvailable, rankedUserStates, isShownAfterLiveGame)

        this.build()
    }

    onEvent(data: any, eventType: GameStateEnums.UIGameEventEnums) {
        switch(eventType) {
            case GameStateEnums.UIGameEventEnums.CanvasResized: this.onCanvasResized(); break
            default: break
        }
    }

    private onCanvasResized() {
        this.destroy()
        this.returnToMapButton.button?.destroy()
        this.build()
    }

    protected extraBuildSteps() {
        if(!this.mainContainer) return

        this.createLogo()
        this.animate()
        this.setCloseTooltipOnContainers()

        this.mainContainer.interactive = true
        this.mainContainer.on('tap', (e: PIXI.FederatedEvent) => {
            e.stopPropagation()
        })
    }

    private setCloseTooltipOnContainers() {
        if(!this.mainContainer) return

        this.mainContainer.on('pointerdown', (e) => {
            this.uiGameManager.toolTipController.closeTip()
        })
        this.returnToMapButton.button?.on('pointerdown', (e) => {
            this.uiGameManager.toolTipController.closeTip()
        })
    }

    protected createTitle() {
        const y = this.sectionSize * 0.85
        const text = this.getEndGameText()
        const color = GameColor.getColor(GameColor.Color.Gray3)

        this.title = new TextView(text, 0, y, this.contentContainer.width, this.sectionSize, this.contentContainer, GameFont.Size.XXXL, color)
        this.title.makeBold()
        this.title.centerXInParent()
    }

    private createLogo() {
        const logoPaddingTop = this.sectionSize / 10
        const ratio = 400 / 800
        const texture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.logo)
        this.logo = new SpriteView(0, logoPaddingTop, this.sectionSize / ratio, this.sectionSize, this.contentContainer, texture)
        this.logo.centerXInParent()
        this.logo.setClickAction(() => {
            this.footerController.handleExitingGame()
        })
    }

    protected createTimeAndTurns() {
        const paddingTop = this.sectionSize * 1.6
        this.timeTurns = new GameEndTimeAndTurnsView(0, paddingTop, this.contentContainer.width,
            this.sectionSize * 0.25, this.contentContainer, this.endGameState)
        this.timeTurns.timeAndTurnsTextView.makeBold()
    }

    protected createReturnToMapButton() {
        const canvas = this.uiGameManager.canvasController
        const w = canvas.itemSize * 2
        const h = canvas.itemSize / 1.5
        const x = canvas.appW - canvas.chatWidth - w * 1.06
        const y = h / 3
        const mapText: TranslationObject = {key: 'strings:game.endGame.cornerButton.map'}
        const mapWithReplayText: TranslationObject = {key: 'strings:game.endGame.cornerButton.mapWithReplay'}
        const returnToMapText = this.isReplayAvailable ? mapWithReplayText : mapText
        const statsText: TranslationObject = {key: 'strings:game.endGame.cornerButton.openStats'}
        this.returnToMapButton.createButton(x, y, w, h, returnToMapText, statsText)
    }

    // eslint-disable-next-line class-methods-use-this
    protected createViewReplayButton() {
        return
    }

    // eslint-disable-next-line class-methods-use-this
    protected createShareButton() {
        return
    }

    protected createStatsController() {
        const statsY = this.sectionSize * 2
        const statsH = this.sectionSize * 3
        this.statsController = new UITGameEndStatsController(this.uiGameManager, this, statsY, statsH, this.contentContainer, this.endGameState)
    }

    protected createFooter() {
        if(!this.mainContainer) return
        if(!this.statsController) return

        const footerY = this.statsController.container.y + this.statsController.container.height
        const footerH = this.sectionSize * 1
        this.footerController.build(footerY, footerH, this.mainContainer)
    }

    protected createEndgameRankedScreen() {
        if(!this.statsController) return

        const myRankedState = this.getPlayerRankedState(this.uiGameManager.gameController.myColor)
        if(myRankedState == undefined) return

        const viewsToHide: HideableEndGameSection[] = [this.statsController, this.shadeWithLine]
        new UIGameRankedEndController(this.uiGameManager, this.contentContainer, this.sectionSize, viewsToHide, this.footerController, myRankedState)
    }

    protected animate() {
        if(!this.statsController) return

        new OpacityAnimationWrapper(this.contentContainer, this.uiGameManager, 0, 1, 700)
        new OpacityAnimationWrapper(this.logo, this.uiGameManager, 0, 1, 300, 800)
        new OpacityAnimationWrapper(this.title, this.uiGameManager, 0, 1, 300, 800)
        new OpacityAnimationWrapper(this.shadeWithLine.container, this.uiGameManager, 0, 1, 300, 2000)
        new OpacityAnimationWrapper(this.statsController.container, this.uiGameManager, 0, 1, 300, 2000)
        if(this.returnToMapButton.button) new OpacityAnimationWrapper(this.returnToMapButton.button, this.uiGameManager, 0, 1, 300, 4000)
        new OpacityAnimationWrapper(this.timeTurns, this.uiGameManager, 0, 1, 300, 4000)
        if(this.itemBoxController != undefined) {
            new OpacityAnimationWrapper(this.itemBoxController.itemBoxContainer, this.uiGameManager, 0, 1, 300, 4000)
        }
        const myRankedUserState = this.getPlayerRankedState(this.uiGameManager.gameController.myColor)
        if(myRankedUserState == undefined) {
            this.footerController.fadeInButtons(4 * TimeUtils.MILLISECONDS)
        }
    }
}
